/*
 * <policy-page> (snippets/policy-layout.liquid)
 *
 * Policy text comes from the Shopify admin and was pasted from several sources,
 * so its markup is inconsistent: some sections use <h2>, others a paragraph that
 * is just <strong>; bullets are typed "•" characters, often several per <p>
 * joined by <br>, with hard line wraps from a PDF. This normalises that into
 * real headings and lists, then builds the "On this page" menu, reading time and
 * scroll progress from the result.
 */
if (!customElements.get('policy-page')) {
  customElements.define(
    'policy-page',
    class PolicyPage extends HTMLElement {
      connectedCallback() {
        this.content = this.querySelector('.policy__content');
        this.document = this.querySelector('[data-policy-document]');
        if (!this.content) return;

        this.removeDuplicateTitle();
        this.normaliseParagraphs();
        this.promoteHeadings();
        this.buildToc();
        this.showReadingTime();
        this.wrapTables();
        this.trackProgress();
      }

      get titleText() {
        const title = this.querySelector('.policy__title');
        return title ? this.normalise(title.textContent) : '';
      }

      normalise(text) {
        return text.replace(/\s+/g, ' ').trim().toLowerCase();
      }

      // Shopify prints its own title, and many policies repeat it as their first line.
      removeDuplicateTitle() {
        this.content.querySelectorAll('.shopify-policy__title').forEach((element) => element.remove());

        const scope = this.content.querySelector('.shopify-policy__body .rte') || this.content;
        const candidate = scope.firstElementChild;
        if (candidate && this.normalise(candidate.textContent) === this.titleText) candidate.remove();
      }

      isBullet(html) {
        return /^\s*(•|&bull;|·)/.test(html);
      }

      stripBullet(html) {
        return html.replace(/^\s*(•|&bull;|·)\s*/, '');
      }

      // Split "<strong>Heading</strong><br>• item<br>continued<br>• item" paragraphs apart.
      normaliseParagraphs() {
        this.content.querySelectorAll('p').forEach((paragraph) => {
          const segments = paragraph.innerHTML
            .split(/<br\s*\/?>/i)
            .map((segment) => segment.replace(/&nbsp;/g, ' ').trim())
            .filter((segment) => segment !== '');

          if (segments.length === 0) {
            paragraph.remove();
            return;
          }

          if (!segments.some((segment) => this.isBullet(segment))) {
            // Plain paragraph: undo hard wraps where the next line carries on mid-sentence.
            if (segments.length > 1) {
              paragraph.innerHTML = segments.reduce((html, segment) =>
                /^[a-z(]/.test(segment) ? `${html} ${segment}` : `${html}<br>${segment}`
              );
            }
            return;
          }

          const fragment = document.createDocumentFragment();
          let list = null;
          let item = null;

          segments.forEach((segment) => {
            if (this.isBullet(segment)) {
              if (!list) {
                list = document.createElement('ul');
                list.className = 'policy__list';
                fragment.appendChild(list);
              }
              item = document.createElement('li');
              item.innerHTML = this.stripBullet(segment);
              list.appendChild(item);
            } else if (item && /^[a-z(]/.test(segment)) {
              item.innerHTML += ` ${segment}`;
            } else {
              list = null;
              item = null;
              const p = document.createElement('p');
              p.innerHTML = segment;
              fragment.appendChild(p);
            }
          });

          paragraph.replaceWith(fragment);
        });

        // Single "• item" paragraphs in a row become one list.
        let currentList = null;
        Array.from(this.content.querySelectorAll('p')).forEach((paragraph) => {
          if (!this.isBullet(paragraph.innerHTML)) {
            currentList = null;
            return;
          }
          const item = document.createElement('li');
          item.innerHTML = this.stripBullet(paragraph.innerHTML);
          const previous = paragraph.previousElementSibling;
          if (!currentList || previous !== currentList) {
            currentList = document.createElement('ul');
            currentList.className = 'policy__list';
            paragraph.before(currentList);
          }
          currentList.appendChild(item);
          paragraph.remove();
        });
      }

      // A short paragraph that is entirely bold is a heading in disguise.
      promoteHeadings() {
        this.content.querySelectorAll('p, h1').forEach((element) => {
          const text = element.textContent.replace(/\s+/g, ' ').trim();
          if (!text) return;

          let isHeading = element.tagName === 'H1';
          if (!isHeading && text.length <= 90) {
            const bold = Array.from(element.querySelectorAll('strong, b'))
              .map((node) => node.textContent)
              .join('')
              .replace(/\s+/g, ' ')
              .trim();
            isHeading = bold === text && !/[.:]$/.test(text);
          }
          if (!isHeading) return;

          const heading = document.createElement('h2');
          heading.textContent = text;
          element.replaceWith(heading);
        });
      }

      slugify(text, used) {
        let slug =
          text
            .toLowerCase()
            .replace(/&/g, 'and')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '') || 'section';
        const base = slug;
        let n = 2;
        while (used.has(slug)) slug = `${base}-${n++}`;
        used.add(slug);
        return slug;
      }

      buildToc() {
        const toc = this.querySelector('[data-policy-toc]');
        const list = this.querySelector('[data-policy-toc-list]');
        this.headings = Array.from(this.content.querySelectorAll('h2'));
        if (!toc || !list || this.headings.length < 3) return;

        const used = new Set();
        this.tocLinks = this.headings.map((heading) => {
          if (!heading.id) heading.id = this.slugify(heading.textContent, used);
          const item = document.createElement('li');
          const link = document.createElement('a');
          link.className = 'policy__toc-link';
          link.href = `#${heading.id}`;
          // Headings like "1. Change-of-Mind Returns" read better without the number in the menu.
          link.textContent = heading.textContent.replace(/^\d+[.)]\s*/, '');
          item.appendChild(link);
          list.appendChild(item);
          return link;
        });

        toc.hidden = false;
        if (window.matchMedia('(max-width: 989px)').matches) toc.open = false;

        if (!('IntersectionObserver' in window)) return;
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const index = this.headings.indexOf(entry.target);
              this.tocLinks.forEach((link, i) => link.classList.toggle('is-active', i === index));
            });
          },
          { rootMargin: '0px 0px -70% 0px' }
        );
        this.headings.forEach((heading) => observer.observe(heading));
      }

      showReadingTime() {
        const meta = this.querySelector('[data-policy-meta]');
        const label = this.querySelector('[data-policy-reading-time]');
        if (!meta || !label) return;
        const words = this.content.textContent.trim().split(/\s+/).length;
        const minutes = Math.max(1, Math.round(words / 220));
        label.textContent = `${minutes} min read`;
        meta.hidden = false;
      }

      wrapTables() {
        this.content.querySelectorAll('table').forEach((table) => {
          if (table.parentElement.classList.contains('policy__table')) return;
          const wrapper = document.createElement('div');
          wrapper.className = 'policy__table';
          table.before(wrapper);
          wrapper.appendChild(table);
        });
      }

      trackProgress() {
        const bar = this.querySelector('[data-policy-progress]');
        if (!bar || !this.document) return;

        let ticking = false;
        const update = () => {
          const rect = this.document.getBoundingClientRect();
          const total = rect.height - window.innerHeight * 0.5;
          const progress = total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 1;
          bar.style.transform = `scaleX(${progress})`;
          ticking = false;
        };

        window.addEventListener(
          'scroll',
          () => {
            if (ticking) return;
            ticking = true;
            window.requestAnimationFrame(update);
          },
          { passive: true }
        );
        update();
      }
    }
  );
}
